import EndScreenContainer from "./end_screen_container"

/**
 * @extends {EndScreenContainer}
 * @export
 */
class RecommendationContainer extends EndScreenContainer {
  /**
   * @param {!RecommendationContainer.Renderer} renderer
   */
  constructor(renderer) {
    super(renderer)

    this.addEvent(
      RecommendationContainer.EventKeys.ClickPageControl,
      pageNumber => {
        renderer.selectedPageNumber = pageNumber
        this.reload()
      }
    )
    this.addEvent(
      RecommendationContainer.EventKeys.ClickRecommendPanel,
      url => window.open(url, '_brank')
    )
  }

  hasRecommendationContents() {
    const recommendationsData = this.rootElement.dataset.recommendations
    if (recommendationsData) {
      const recommendations = JSON.parse(recommendationsData)
      const filtered = recommendations.filter(recommendation => recommendation.url.length !== 0)
      return filtered.length !== 0
    }

    return false
  }
}

/**
 * @implements {EndScreenContainer.Renderer}
 */
RecommendationContainer.Renderer = class extends EndScreenContainer.Renderer {
  constructor() {
    super()
    this.selectedPageNumber = 0

    this._numberOfRow = 2
    this._numberOfColumn = 2
    this._splitNumber = this._numberOfRow * this._numberOfColumn
    this._panelMarginRatio = 0.1
    this._luminanceForImage = {}
  }

  /**
   * @override
   */
  willInsertContainerElement(recommendationContainer) {
    const container = document.createElement('div')
    container.classList.add('recommendation-container')
    return container
  }

  /**
   * @override
   */
  render(recommendationContainer, data) {
    const recommendations = JSON.parse(data.recommendations)

    // url が設定されていない物を除外
    const filteredRecommendations = recommendations.filter(recommendation => {
      return recommendation.url.length !== 0
    })

    const recommendPanelContainer = document.createElement('div')
    recommendPanelContainer.classList.add('recommend-panel-container')
    recommendationContainer.container.appendChild(recommendPanelContainer)

    // Create RecommendPanel

    const containerWidth = recommendPanelContainer.clientWidth
    const containerHight = recommendPanelContainer.clientHeight
    const panelMargin = containerWidth * this._panelMarginRatio
    const panelWidth = (containerWidth - panelMargin) / this._numberOfColumn
    const panelHeight = (containerHight - panelMargin) / this._numberOfRow
    const startIndex = this.selectedPageNumber * this._splitNumber
    const endIndex = startIndex + this._splitNumber
    const displayRecommendations = filteredRecommendations.slice(startIndex, endIndex)
    displayRecommendations.forEach((recommendation, index) => {
      const panel = this._createRecommendPanel(
        recommendation.url,
        recommendation.link,
        recommendation.image_url
      )
      const column = index % this._numberOfColumn
      const row = parseInt(index / this._numberOfColumn)

      panel.style.width = `${panelWidth}px`
      panel.style.height = `${panelHeight}px`
      panel.style.left = `${panelMargin * 0.5 / this._numberOfColumn + (panelWidth + panelMargin / this._numberOfColumn) * column}px`
      panel.style.top = `${panelMargin / this._numberOfRow + (panelHeight + panelMargin / this._numberOfRow) * row}px`
      panel.addEventListener('click', (event) =>
        recommendationContainer.trigger(
          RecommendationContainer.EventKeys.ClickRecommendPanel,
          recommendation.url
        )
      )
      recommendPanelContainer.appendChild(panel)
    })

    // Create PageControls

    const pageNumbers = parseInt(Math.ceil(filteredRecommendations.length / this._splitNumber))
    if (pageNumbers > 1) {
      // 1ページで収まらない場合に限り PageControl を表示

      const pageControls = document.createElement('div')
      pageControls.classList.add('recommend-page-controls')
      for (const pageNumber of Array(pageNumbers).keys()) {
        const pageControl = this._createPageControl(pageNumber)
        pageControl.addEventListener('click', (event) =>
          recommendationContainer.trigger(
            RecommendationContainer.EventKeys.ClickPageControl,
            pageNumber
          )
        )
        pageControls.appendChild(pageControl)
      }
      recommendationContainer.container.append(pageControls)
    }
  }

  /**
   * @param {string} url
   * @param {string} linkText
   * @param {string} imageUrl
   * @returns {HTMLElement}
   * @private
   */
  _createRecommendPanel(url, linkText, imageUrl) {
    const image = document.createElement('img')
    image.src = imageUrl || ''

    const title = document.createElement('figcaption')
    title.innerHTML = linkText

    const panel = document.createElement('figure')
    panel.dataset.url = url
    panel.classList.add('recommend-panel')
    panel.appendChild(image)
    panel.appendChild(title)

    return panel
  }

  /**
   * @param {number} pageNumber
   * @returns {HTMLElement}
   */
  _createPageControl(pageNumber) {
    const pageControl = document.createElement('a')
    pageControl.dataset.pageNumber = pageNumber
    pageControl.classList.add('page-control')
    if (pageNumber === this.selectedPageNumber) {
      pageControl.classList.add('selected')
    }

    return pageControl
  }

  /**
 * @param {string} src
 * @returns {Promise<Image>}
 * @private
 */
  _loadImage(src) {
    return new Promise((resolve, reject) => {
      if (!src) resolve(null)

      const image = new Image();
      image.addEventListener('load', () => resolve(image), { once: true })
      image.addEventListener('error', (event) => reject(event), { once: true })
      image.src = src
      image.crossOrigin = 'Anonymous';
    })
  }

  /**
   * @async
   * @param {str} imageUrl
   * @returns {Promise<Number>}
   * @private
   */
  async _getLuminanceForImage(imageUrl) {
    const image = await this._loadImage(imageUrl).catch(event => console.log(event.type))
    if (image) {
      const canvas = document.createElement('canvas');
      canvas.width = image.width
      canvas.height = image.height

      const canvasContext = canvas.getContext('2d')
      canvasContext.drawImage(image, 0, 0, canvas.width, canvas.height)
      const imageData = canvasContext.getImageData(0, 0, canvas.width, canvas.height)
      const data = imageData.data
      const rgbaUnit = 4
      let total = 0
      for (let i = 0; i < data.length; i += rgbaUnit) {
        // 輝度値算出（ITU-R Rec BT.709）
        total += data[i] * 0.21 + data[i + 1] * 0.72 + data[i + 2] * 0.07
      }

      // 輝度値の平均を正規化して返す
      return new Number(total / (data.length / rgbaUnit) / 255)
    }

    return new Number(1.0)
  }
}

RecommendationContainer.EventKeys = {
  ClickPageControl: Symbol('clickPageControl'),
  ClickRecommendPanel: Symbol('clickRecommendPanel')
}

export default RecommendationContainer
